<template>
  <!-- .message-content start -->
  <div
    @click="messageContentDivClick"
    @scroll="scrolling"
    :style="`height: ${windowHeight - 105 + 'px'}`"
    ref="messageContent"
    id="message-content"
    class="overflow-x-scroll h-full p-[15px] bg-theme-accent message-texture dark:bg-black bg-contain flex flex-col-reverse gap-[10px]"
  >
    <MessageBox
      @sendMessage="sendMessage"
      :allMessages="allMessages"
      :loaders="loaders"
    />
    <ScrollToBottom
      v-if="showScrollToBottom"
      @handleScrollToBottom="handleScrollToBottom"
      :scrollTop="scrollTop"
    />
  </div>
  <!-- .message-content finish -->
  <!-- .send-message start -->
  <div
    id="send-message"
    class="h-[60px] w-full px-[15px] bg-theme-accent dark:bg-black flex justify-center items-center gap-3"
  >
    <!-- CameraRear start -->
    <CameraRear
      v-if="cameraRearEqual"
      :sendMessageWithImage="sendMessageWithImage"
      :userMessageType="allMessages[0]?.userMessageType"
      :attachment="
        allMessages[0]?.attachments.find(
          (attachment) => attachment.type == attachmentTypes.TEXT
        )
      "
    />
    <!-- CameraRear finish -->

    <!-- CameraFront start -->
    <!-- CameraFront finish -->

    <SendMessage
      v-if="!cameraRearEqual"
      :loaders="loaders"
      @sendMessage="sendMessage"
      :allMessages="allMessages"
    />
  </div>
  <!-- .send-message finish -->
</template>

<script setup>
  // Components
  import MessageBox from "@/pages/components/MessageBox.vue"
  import SendMessage from "@/pages/components/SendMessage.vue"
  import ScrollToBottom from "@/components/composables/ScrollToBottom.vue"
  import CameraRear from "@/pages/components/messages/cameras/CameraRear.vue"

  // Utils
  import {
    sendMessageTypes,
    messageTypes,
    userMessageTypes,
    attachmentTypes,
  } from "@/utils/enums"
  import services from "@/services/index"
  import { useSound } from "@vueuse/sound"
  import { MessageModel, PaginationMessageModel } from "@/store/message/model"
  import { getRouteQueryParams, getDate } from "@/utils/settings"
  import { userSettings } from "@/store/userSettings.js"
  import { localSetItem, localGetItem } from "@/utils/local.js"

  // Vue
  import {
    computed,
    onMounted,
    reactive,
    ref,
    onUnmounted,
    provide,
    watch,
  } from "vue"

  // Public
  import send from "@/../public/assets/sounds/send.mp3"

  import { useWebSocket } from "@/lib/useWebSocket"

  import { useWakeLock } from "@vueuse/core"

  import { useRoute, useRouter } from "vue-router"

  const { isSupported, isActive, request, release } = useWakeLock()

  const { connectionError } = useWebSocket()

  const toggleWakeLock = async () => {
    if (isActive.value) {
      // await release()
    } else {
      try {
        await request()
      } catch (error) {
        console.error("Wake Lock hatası:", error)
      }
    }
  }

  // Route
  const route = useRoute()
  const router = useRouter()

  // Sounds
  const sendSoundEffect = useSound(send)

  const messageContent = ref(null)

  const fakeAttachment = {
    text: "My dear Tuğberk, today is October 6th 2024. I am now starting your reading with the compliments of FTC Cafe and Fortune Teller Coffee.\n\n*I foresee a challenge approaching swiftly.* Do you feel an unsettling pressure in your professional life lately? The symbol of the glove appears on the rim of your cup, indicating a challenge that will soon confront you. This challenge is not merely an obstacle but a golden opportunity to showcase your resilience and inner strength. It calls for courage and persistence. Your inherent passion and drive will be your greatest allies. Remember to channel your energy wisely, as you navigate the situation like a skilled artisan crafting his masterpiece. Embrace this opportunity to grow, Tuğberk.\n\n*I expect great fortune touching every aspect of your life.* A deep vision reveals the symbol of the fish resting in the middle of your cup, promising an era of good fortune in the form of health, wealth, and happiness. Your physical and mental well-being will strengthen, enriching your experiences. Friends and family will bring warmth and joy, encouraging connections that emit vibrant energy. Enter this season with gratitude and enthusiasm, for it will soon shower you with abundance and serendipitous encounters. Treasure these days as pearls of joy woven into the fabric of your life, for they will beautifully illuminate your path ahead, Tuğberk.\n\n*I predict unexpected success diving from unforeseen threats.* Hidden in the quiet depths of your cup lies the lamp, meaning trouble that threatens to arise but is ultimately averted by the wisdom you possess. As the winds of October whisper their secrets, remain cautious and insightful. Your learned wisdom and instinct will guide you through uncertain terrains. Relief will come through your own intuition, shining a beam of understanding that steers you toward prosperity. Trust your inner compass to light the way in times of darkness, turning potential chaos into enduring triumph in your remarkable journey.\n\nMay these visions anchor your months ahead, bestowing clarity and confidence upon you, dear Tuğberk.",
    link: "https://firebasestorage.googleapis.com/v0/b/fortune-teller-coffee.appspot.com/o/voices%2Fsessions%2F3ceee691-bfa3-47dc-8398-9da47470f491.mp3?alt=media",
    title:
      "I just had a session with Laila the AI coffee fortune-teller and this is what she told me!",
  }

  function EmptyMessage() {
    this.query = ""
    this.language = userSettings.language
  }

  function UserEmptyMessage() {
    this.messageType = messageTypes.USER
    this.message = ""
    this.userMessageType = userMessageTypes.TEXT
    this.timestamp = new Date()
    this.timestamp_t = getDate(this.timestamp)
    this.read = false
  }

  const windowHeight = ref(window.innerHeight)

  const handleResize = () => {
    windowHeight.value = window.innerHeight
    document.getElementById("message-content").click() // Örnek olarak bir elementin click olayını tetikleyelim
    handleScrollToBottom()
  }

  // Scroll
  const showScrollToBottom = ref(false)
  const scrollTop = ref(0)

  const formData = reactive(new EmptyMessage())

  const loaders = reactive({
    sendMessage: true,
    firstMessage: true,
    pagination: false, // Pagination loader
  })

  const allMessages = ref([])

  let previousSessionId = ref(null) // Sonraki sayfa için previous_session_id
  let isPaginating = ref(false) // Aynı anda birden fazla istek atılmasını engellemek için

  const cameraEquals = [
    userMessageTypes.CAMERA_REAR,
    userMessageTypes.CUP_ONE_UPLOAD,
    userMessageTypes.CUP_TWO_UPLOAD,
    userMessageTypes.CUP_THREE_UPLOAD,
    userMessageTypes.CUP_FOUR_UPLOAD,
    userMessageTypes.CUSTOM_LOTTIE,
  ]

  const cameraRearEqual = computed(() => {
    return (
      cameraEquals.includes(allMessages.value[0]?.userMessageType) &&
      allMessages.value[0].messageWriter
    )
  })

  // Scrolling
  const scrolling = (e) => {
    scrollTop.value = e.target.scrollTop
    if (scrollTop.value < -120) showScrollToBottom.value = true
    else showScrollToBottom.value = false
    if (
      Math.abs(
        e.target.scrollHeight -
          Math.abs(scrollTop.value) -
          e.target.clientHeight
      ) <= 1 &&
      previousSessionId.value &&
      !isPaginating.value
    ) {
      isPaginating.value = true
      loaders.pagination = true
      getAllChatMessages(previousSessionId.value, true).finally(() => {
        loaders.pagination = false
        isPaginating.value = false
      })
    }
  }

  const handleScrollToBottom = () => {
    messageContent.value.scrollTop = 0
  }

  // First message
  const firstMessage = () => {
    let customQuery = route.query.dfKey
    if (customQuery) router.replace({ query: {} })

    loaders.sendMessage = true
    sendMessage({
      query: !customQuery ? userSettings.query : customQuery,
      type: sendMessageTypes.firstMessage,
      isFirstMessage: true,
    })
  }

  // Send message
  const sendMessage = ({
    query,
    type,
    message,
    isFirstMessage = false,
    button,
  }) => {
    toggleWakeLock()
    if (type === sendMessageTypes.readyAnswers) {
      message.readyAnswersForReplySent = true
    }

    if (userSettings.messageSound && !isFirstMessage) sendSoundEffect.play()

    loaders.sendMessage = true

    // Kullanıcının mesajı
    if (sendMessageTypes.firstMessage != type) {
      const userMessage = new UserEmptyMessage()
      userMessage.message = query
      userMessage.lailaMessageType = allMessages.value[0]?.userMessageType
      allMessages.value.unshift(userMessage)
    }

    // Chatbot gönderdiğimiz data
    const copyData = new EmptyMessage()
    copyData.query = query
    copyData.user_message_type = userMessageTypes.TEXT
    copyData.button = button

    const lailaMessages = allMessages.value.filter((item) => {
      if (item.messageType == messageTypes.CHATBOT) {
        return item
      }
    })

    copyData.intent = lailaMessages[0]?.intent

    sendMessageForDialogFlow({ copyData, isFirstMessage })
  }

  // Sendmessage for dialogflow
  const sendMessageForDialogFlow = ({
    copyData,
    isImage = false,
    isFirstMessage = false,
  }) => {
    console.log("allmessages.value", allMessages.value)
    if (
      allMessages.value[1]?.attachments &&
      allMessages.value[1]?.attachments[0]?.type === attachmentTypes.LOTTIE
    )
      messageReading({
        message: allMessages.value[0],
        status: true,
        delay: 0,
      })

    handleScrollToBottom()
    services
      .sendMessage({
        sessionId: userSettings.session_id,
        payload: copyData,
        cupId: getRouteQueryParams(window.location.pathname),
      })
      .then((res) => {
        if (allMessages.value[0]?.messageType == messageTypes.USER)
          messageReading({ message: allMessages.value[0], status: true })
        setTimeout(
          () => {
            allMessages.value.unshift(new MessageModel(res.data))
            loaders.sendMessage = false
            loaders.firstMessage = false
          },
          isFirstMessage ? 0 : 500
        ) // 2 saniye sonra tetiklenecek

        // connect()
      })
      .catch((err) => {
        if (err.response?.status == "401") {
          firstStart()
        }
        if (allMessages.value[0]?.messageType == messageTypes.USER)
          messageReading({ message: allMessages.value[0], status: false })
        loaders.sendMessage = false
        loaders.firstMessage = false
      })
  }

  //
  const sendMessageWithImage = ({ image, query }) => {
    loaders.sendMessage = true

    // Kullanıcının mesajı
    const userMessage = new UserEmptyMessage()
    userMessage.userMessageType = userMessageTypes.IMAGE
    userMessage.message = image.imagePreview
    userMessage.localImage = image.localImage
    allMessages.value.unshift(userMessage)

    // Chatbot gönderdiğimiz data
    const copyData = new EmptyMessage()
    copyData.query = query
    copyData.user_message_type = userMessageTypes.IMAGE

    sendMessageForDialogFlow({ copyData, isImage: true })
  }

  // getAllChatMessages

  const getAllChatMessages = async (
    sessionId = userSettings.session_id,
    isPreview = false
  ) => {
    await services
      .getChatMessages({ sessionId, isPreview })
      .then((res) => {
        res.data.history.map((item, index) => {
          allMessages.value.push(
            new PaginationMessageModel(item, res.data.history[0], isPreview)
          )
          loaders.sendMessage = false
          loaders.firstMessage = false
        })

        // previous_session_id bilgisini sakla
        previousSessionId.value = res.data.previous_session_id || null
        if (res.data.previous_session_id && !isPreview) {
          getAllChatMessages(res.data.previous_session_id, true)
        }
      })
      .catch((err) => {
        loaders.sendMessage = false
        console.log("err", err)
      })
  }

  const firstStart = async () => {
    console.log("userSettings", userSettings)
    await userLocalSettings()
    await getAllChatMessages(userSettings.session_id, false)

    if (allMessages.value.length > 0) return
    firstMessage()
  }

  // Kullanıcının ayarlarını yaptığımız fonksyion

  const userLocalSettings = async () => {
    await userSettings.updateAsyncData()
    if (!localGetItem("userSettings")) {
      return localSetItem("userSettings", JSON.stringify(userSettings))
    } else {
      const newObj = JSON.parse(localGetItem("userSettings"))

      newObj.query = userSettings.query
      newObj.cup_id = userSettings.cup_id
      newObj.session_id = userSettings.session_id
      newObj.token = userSettings.token

      if (!newObj.userTheme) {
        if (
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches
        ) {
          newObj.userTheme = "dark"
        } else {
          newObj.userTheme = "light"
        }
      }

      delete newObj.isGuest
      if (!newObj.cuppScanCount) newObj.cuppScanCount = 0
      Object.assign(userSettings, newObj)

      if (newObj.userTheme === "dark")
        document.documentElement.classList.add("dark")
      localSetItem("userSettings", JSON.stringify(newObj))
    }
  }

  // Okundu bilgisi

  const messageReading = ({ message, status, delay = 300 }) => {
    setTimeout(() => {
      if (message.read) return
      message.read = status
    }, delay) // 2 saniye sonra tetiklenecek
  }

  onMounted(async () => {
    window.addEventListener("resize", handleResize)
    handleScrollToBottom()
    await firstStart()
  })

  onUnmounted(() => {
    window.removeEventListener("resize", handleResize)
  })

  watch(
    () => connectionError,
    (newVal) => {
      if (newVal) allMessages.value.splice(0, 1)
      sendMessage({
        query: "bypass-give-voice",
        type: sendMessageTypes.firstMessage,
        isFirstMessage: true,
      })
    },
    { deep: true }
  )

  //
</script>
